import { DateTime } from 'luxon';
import { useState, useCallback } from 'react';
import {
  AiChatConfigurationPayload,
  AiChatFieldPayload,
} from '../../../../apis/facades/useDocumentationApiFacade';
import { AiChatContextType, ChatCompletionRequestMessageRole } from '@server/ai';
import { AiChatMessage } from '../../../common/ai-chat/aiChatTypes';
import usePaywall from '../../../dashboard/paywall/usePaywall';
import { telemetry } from '../../../../telemetry';
import { useAiChat } from '../../../common/ai-chat/useAiChat';
import { uniqueId } from '../../../../lib/uniqueId';

interface OnConfirmBaseProps {
  message: string;
  crmOrgId: string;
}

interface OnConfirmForConfigurationProps extends OnConfirmBaseProps {
  metadataId: string;
}

interface OnConfirmForFieldsProps extends OnConfirmBaseProps {
  objectName: string;
  fieldName: string;
}

export const useConfigurationChat = () => {
  const { onSendConfigurationMessage, onSendFieldMessage } = useAiChat();
  const { isPaywallActive } = usePaywall();

  const [sessionId] = useState(uniqueId());
  const [messages, setMessages] = useState<AiChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const disableInput = isPaywallActive;

  const onConfirmOrgChatForConfiguration = useCallback(
    async ({ message, crmOrgId, metadataId }: OnConfirmForConfigurationProps) => {
      error && setError('');

      const payload: AiChatConfigurationPayload = {
        message,
        sessionId,
        isFirstMessage: messages.length === 0,
        crmOrgId,
        context: {
          metadataId,
          type: AiChatContextType.CONFIGURATION,
        },
      };

      const createdAt = DateTime.now().toISO() ?? '';
      const _messages: AiChatMessage[] = [
        ...messages,
        { role: ChatCompletionRequestMessageRole.USER, content: message, createdAt },
      ];
      setMessages(_messages);

      if (isPaywallActive) {
        setMessages([
          ..._messages,
          {
            role: ChatCompletionRequestMessageRole.ASSISTANT,
            content: 'Not included in Free tier.',
            createdAt,
          },
        ]);
        return;
      }

      setIsLoading(true);

      try {
        const _response = await onSendConfigurationMessage({
          payload,
        });

        if (_response.error) {
          setError(_response.error);
          setIsLoading(false);
          return;
        }

        const lastResponse = _response.data.pop();
        if (lastResponse) {
          setMessages([..._messages, lastResponse]);
        }
      } catch (e) {
        setError('Something went wrong, please try again later');
        telemetry.captureError(e);
      }

      setIsLoading(false);
    },
    [error, messages, onSendConfigurationMessage, isPaywallActive, sessionId],
  );

  const onConfirmOrgChatForFields = useCallback(
    async ({ message, crmOrgId, objectName, fieldName }: OnConfirmForFieldsProps) => {
      error && setError('');
      const createdAt = DateTime.now().toISO() ?? '';
      const _messages: AiChatMessage[] = [
        ...messages,
        { role: ChatCompletionRequestMessageRole.USER, content: message, createdAt },
      ];
      setMessages(_messages);

      if (isPaywallActive) {
        setMessages([
          ..._messages,
          {
            role: ChatCompletionRequestMessageRole.ASSISTANT,
            content: 'Not included in Free tier.',
            createdAt,
          },
        ]);
        return;
      }

      setIsLoading(true);
      const payload: AiChatFieldPayload = {
        message,
        sessionId,
        context: {
          type: AiChatContextType.FIELD,
          objectName,
          fieldName,
        },
        isFirstMessage: messages.length === 0,
        crmOrgId,
      };

      try {
        const _response = await onSendFieldMessage({
          payload,
        });

        if (_response.error) {
          setError(_response.error);
          setIsLoading(false);
          return;
        }

        const lastResponse = _response.data.pop();
        if (lastResponse) {
          setMessages([..._messages, lastResponse]);
        }
      } catch (e) {
        setError('Something went wrong, please try again later');
        telemetry.captureError(e);
      }

      setIsLoading(false);
    },
    [messages, onSendFieldMessage, isPaywallActive, sessionId, error],
  );

  return {
    isLoading,
    messages,
    error,
    onConfirmOrgChatForFields,
    onConfirmOrgChatForConfiguration,
    disableInput,
  };
};
